import { GridApi, GridReadyEvent } from 'ag-grid-community';
import React, { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Column, SelectionsProps } from 'components/atoms/grid';
import { TaskGrid } from 'components/organisms/task-grid';
import { PermissionRuleName } from 'constant';
import { RootState } from 'data';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { logger } from 'services';
import { Empty } from 'stores/core';
import { TaskField } from 'stores/fields';
import { queueMacrotask } from 'utils/macrotask';
import { GridChangeEvent } from '../grid-custom-column';
import { SingleSelectField, TextField } from './fields';
import { FilterFunction } from './filter-functions';
import { useCreateTask } from './use-create-task';
import { useDeleteTasks } from './use-delete-tasks';
import { PartialValue, useUpdateTasks } from './use-update-tasks';

interface Props extends Omit<SelectionsProps, 'multiSelect' | 'isSelectable'> {
  initialized: boolean;
  filterFunction: FilterFunction | null;
  projectId: number;
  fields: Record<number, TaskField>;
  projectField?: SingleSelectField & TaskField;
  taskNameField?: TextField & TaskField;
  gridApi?: GridApi;
  handleGridChange: (data: GridChangeEvent) => void;
  onGridReady: (event: GridReadyEvent) => void;
  columns: Column[];
}

export const TaskList: FC<Props> = ({
  filterFunction,
  initialized,
  projectId,
  fields,
  projectField,
  taskNameField,
  selected,
  onSelectionChange,
  selectableRef,
  gridApi,
  handleGridChange,
  onGridReady,
  columns,
}) => {
  const { updateMultipleTasks, updateSingleTask } = useUpdateTasks();
  const createTask = useCreateTask();
  const deleteTasks = useDeleteTasks(false);
  const { byProjectId, byId } = useSelector(
    ({ tasks_v2 }: RootState) => tasks_v2,
  );
  const ids = useMemo(
    () => byProjectId[projectId] ?? Empty(),
    [byProjectId, projectId],
  );
  const tasks_loading = ids[0] !== 'loaded' && ids[0] !== 'refreshing';
  const onCreate = useCallback(
    (expectations: PartialValue[]) =>
      queueMacrotask(() => {
        if (projectField == null || taskNameField == null) {
          logger.error('field_missing', {
            error: new Error('project field or task name field missing'),
            projectField,
            taskNameField,
          });

          return;
        }
        createTask([
          { field: projectField, to: projectId },
          { field: taskNameField, to: expectations[0].to as string },
        ]);
      }),
    [createTask, projectId, projectField, taskNameField],
  );

  const { isAllowedForCurrentUser } = usePermissionForRule();

  if (tasks_loading) {
    return null;
  }

  return (
    <TaskGrid
      columns={columns}
      fields={fields}
      filterFunction={filterFunction}
      ids={ids[1] ?? []}
      loading={!initialized || tasks_loading}
      lookup={byId}
      updateSingleTask={updateSingleTask}
      updateMultipleTasks={updateMultipleTasks}
      onCreate={onCreate}
      onDelete={deleteTasks}
      provisionalRow={
        !isAllowedForCurrentUser(PermissionRuleName.TaskCreateAction)
          ? undefined
          : {
              project: projectId,
              taskId: 'provisional',
              taskTree: [{ taskId: 'provisional' }],
            }
      }
      selected={selected}
      onSelectionChange={onSelectionChange}
      selectableRef={selectableRef}
      onReady={onGridReady}
      isPaginationEnabled={false}
      gridApi={gridApi}
      handleGridChange={handleGridChange}
    />
  );
};
